import React, { useState, useEffect, useMemo } from 'react';
import { reject } from 'lodash/fp';
import { useToggle } from 'hooks';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import RemoteAutoComplete from 'components/shared/RemoteAutoComplete';
import { Icon } from 'ui';
import { setQueryString } from 'utils/url';
import filterSuggestions from 'utils/filterSuggestions';
import { updateItem } from 'utils/fp';
import OwnershipOption from './OwnershipOption';
import OwnersList from './OwnersList';
import OwnersListItem from './OwnersListItem';
import { canEditOnly, Permission } from './permission';
import { normalizeOwnershipsAsOwners } from './normalizeOwnershipsAsOwners';
import OwnershipOptions from './OwnershipOptions';

/**
 * Fetches a list of all automatic owners asynchronously
 */
function useFetchAutomaticOwners() {
  const [owners, setOwners] = useState([]);
  useEffect(() => {
    request
      .get(routes.OWNERSHIPS_PEOPLE, { ownership_type: 'automatic_ownership' })
      .then(response => setOwners(response.owners));
  }, []);
  return owners;
}

const LessonOwnership = ({
  initialDirectOwnerships,
  policies,
  ownableId,
  ownableType,
}) => {
  const automaticOwners = useFetchAutomaticOwners();
  const [addOwnerLoading, toggleAddOwnerLoading] = useToggle();
  const [directOwnerships, setDirectOwnerships] = useState(
    initialDirectOwnerships,
  );

  /**
   * A list of owners with their corresponding ownerships
   */
  const ownershipsByOwners = useMemo(
    () =>
      normalizeOwnershipsAsOwners(directOwnerships, 'unique_identifier').sort(
        (a, b) => (a.name > b.name ? 1 : -1),
      ),
    [directOwnerships],
  );

  /**
   * Owners with direct ownerships on the lesson itself **WITHOUT** any
   * inherited ownerships via a path
   */
  const explicitOwners = useMemo(
    () =>
      ownershipsByOwners.filter(owner =>
        owner.ownerships.every(ownership => ownership.ownable_id === ownableId),
      ),
    [ownableId, ownershipsByOwners],
  );

  /**
   * Owners with at least one ownership inherited via a path
   */
  const inheritedOwners = useMemo(
    () =>
      ownershipsByOwners.filter(owner =>
        owner.ownerships.some(ownership => ownership.ownable_id !== ownableId),
      ),
    [ownableId, ownershipsByOwners],
  );

  /**
   * A function that filters any existing owners from a list of suggestions
   */
  const filterExistingOwners = useMemo(
    () =>
      filterSuggestions(
        [...automaticOwners, ...explicitOwners, ...inheritedOwners],
        'unique_identifier',
      ),
    [automaticOwners, explicitOwners, inheritedOwners],
  );

  const renderSuggestion = suggestion => {
    if (suggestion.type === 'User') {
      return suggestion.name;
    }
    return (
      <span>
        <Icon name="group" className="mr-8 c-brand-gray-dark" />
        {suggestion.name}
      </span>
    );
  };

  async function handleSuggestionSelected(suggestion) {
    try {
      toggleAddOwnerLoading();
      const route = routes.LESSON_ADD_OWNER(ownableId);
      const ownership = await request.postJSON(route, {
        ownableId,
        ownableType,
        ownerId: suggestion.id,
        ownerType: suggestion.type,
      });
      setDirectOwnerships(ownerships => [...ownerships, ownership]);
    } finally {
      toggleAddOwnerLoading();
    }
  }

  async function handleRemoveOwnership(ownership) {
    if (
      // eslint-disable-next-line no-alert, no-restricted-globals
      confirm(
        t('ownership.share.remove_confirmation', {
          name: ownership.owner.name,
        }),
      )
    ) {
      try {
        const route = routes.LESSON_REMOVE_OWNER(ownableId, ownership.owner.id);
        const response = await request.delete(route, {
          ownerId: ownership.owner.id,
          ownerType: ownership.owner.type,
        });
        setDirectOwnerships(ownerships => reject(ownership, ownerships));
        pubsub.publish('lesson-owner-removed', ownership.owner.id);
        if (response.redirect) {
          window.location.href = response.redirect;
        }
      } catch {} // eslint-disable-line no-empty
    }
  }

  async function toggleLessonActivityPermission(ownership) {
    const permission =
      ownership.permission === Permission.EditOnly
        ? Permission.EditAndViewActivity
        : Permission.EditOnly;
    try {
      await request.put(routes.OWNERSHIP(ownership.id), { permission });
      setDirectOwnerships(ownerships =>
        updateItem(ownerships, { id: ownership.id }, { permission }),
      );
    } catch {} // eslint-disable-line no-empty
  }

  return (
    <div id="ownerships">
      <h3 className="Module-heading SectionHeading">
        {t('ownership.share.title.enabled')}
        <Icon
          name="info"
          className="Module-headingIcon"
          data-toggle="tooltip"
          data-original-title={t('ownership.share.tooltip_groups')}
        />
      </h3>
      {policies.add_owner && (
        <RemoteAutoComplete
          className="SidebarModule-input"
          filterSuggestions={filterExistingOwners}
          placeholder={t('ownership.share.placeholder_groups')}
          getSuggestionValue={suggestion => suggestion.name}
          mapResponseToSuggestions={response => response.owners}
          onSuggestionSelected={handleSuggestionSelected}
          renderSuggestion={renderSuggestion}
          url={setQueryString(routes.OWNERSHIPS_PEOPLE, { limit: 10 })}
          disabled={addOwnerLoading}
        />
      )}
      <section>
        {/* Automatic Owners */}
        <OwnersList
          id="automatic_owners_list"
          heading={t('ownership.heading.automatic_owners')}
          owners={automaticOwners}
          collapsed
        >
          <OwnersListItem />
        </OwnersList>

        {/* Inherited owners with inherited ownership via a path */}
        {!!inheritedOwners.length && (
          <OwnersList
            id="inherited_owners_list"
            heading={t('ownership.heading.inherited_owners')}
            owners={inheritedOwners}
          >
            <OwnersListItem />
          </OwnersList>
        )}

        {/* Explicit owners with ownerships on the lesson without inherited ownerships */}
        <OwnersList
          id="explicit_owners"
          heading={t('ownership.heading.shared_owners')}
          owners={explicitOwners}
        >
          <OwnersListItem>
            {owner => (
              <>
                {policies.update_ownership &&
                  policies.remove_ownership &&
                  owner.type !== 'Group' && (
                    <OwnershipOptions>
                      <OwnershipOption
                        icon="remove"
                        onClick={handleRemoveOwnership}
                      >
                        {t('ownership.remove')}
                      </OwnershipOption>
                      <OwnershipOption
                        icon="reporting"
                        onClick={toggleLessonActivityPermission}
                      >
                        {canEditOnly(owner)
                          ? t('ownership.reporting_permissions.lessons.share')
                          : t('ownership.reporting_permissions.lessons.revoke')}
                      </OwnershipOption>
                    </OwnershipOptions>
                  )}
                {((owner.type === 'Group' && policies.remove_ownership) ||
                  (!policies.update_ownership &&
                    policies.remove_ownership)) && (
                  <button
                    type="button"
                    className="u-resetBtn quicklist-remove"
                    onClick={() => handleRemoveOwnership(owner.directOwnership)}
                  >
                    <Icon name="remove" />
                  </button>
                )}
              </>
            )}
          </OwnersListItem>
        </OwnersList>
      </section>
    </div>
  );
};

export default props => <LessonOwnership {...props} />;
